'use client';
import classNames from 'classnames';
import { TypeCtaSimple } from '@fm-nx/contentful-types';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import AnimationFadeIn from '../animation-fade-in/animation-fade-in';
import Button from '../button/button';
import Form from '../form/form';
import { useBreakpoint } from '../hooks/useResponsive';
import TextMarkdown from '../text-markdown/text-markdown';

type CtaFullSimpleProps = {
    ctaSimple: TypeCtaSimple<'WITHOUT_UNRESOLVABLE_LINKS'>;
    formId?: 'apply' | 'contact';
};

export const CtaFullSimple = (props: CtaFullSimpleProps) => {
    const { ctaSimple } = props;
    const updatedBlock = useContentfulLiveUpdates(ctaSimple);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedBlock?.sys?.id });
    const { fields } = updatedBlock;

    const { isMobile } = useBreakpoint();
    // if (!fields) return null;

    const { headerText, paragraphText, overline, form } = fields;

    const hasText = overline || headerText || paragraphText;
    const hasButton = !!fields.button;
    const hasForm = !!form;

    return (
        <div>
            <svg
                className="w-full h-[15.9vw] -mb-0.5 sm:-mb-1 lg:hidden"
                width="390"
                height="62"
                viewBox="0 0 390 62"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    className="w-full fill-ivory-100"
                    d="M390 61.3594L0 61.3593L1.6255e-07 59.5C1.6255e-07 59.5 73.096 0.000586462 195 0.000597119C316.904 0.000607776 390 59.5 390 59.5L390 61.3594Z"
                />
            </svg>

            <div className="bg-ivory-100 pt-6 lg:pt-32 lg:rounded-t-[40px] xl:rounded-t-[60px] 2xl:rounded-t-[80px]">
                <div className="block mx-6 md:mx-0 md:grid md:grid-cols-14">
                    <div className="col-span-12 col-start-2 md:col-span-8 md:col-start-4 xl:col-span-6 xl:col-start-5">
                        <div className="flex items-center justify-center">
                            <AnimationFadeIn threshold={isMobile ? 0.05 : 0.15}>
                                <>
                                    {hasText && (
                                        <div className="space-y-6">
                                            {!!overline && (
                                                <h5
                                                    className={`h5 text-center overline`}
                                                    {...inspectorProps({ fieldId: 'overline' })}
                                                >
                                                    {overline}
                                                </h5>
                                            )}
                                            {headerText && (
                                                <div className="">
                                                    <h4
                                                        className="text-center h-xl"
                                                        {...inspectorProps({ fieldId: 'headerText' })}
                                                    >
                                                        {headerText}
                                                    </h4>
                                                </div>
                                            )}
                                            {paragraphText && (
                                                <div
                                                    className="text-center"
                                                    {...inspectorProps({ fieldId: 'paragraphText' })}
                                                >
                                                    <TextMarkdown text={paragraphText} />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {fields.button && (
                                        <div className="flex items-center justify-center py-8">
                                            <Button button={fields.button} buttonType="outline-dark" />
                                        </div>
                                    )}

                                    {hasForm && (
                                        <div className={classNames({ 'mt-20': hasText || hasButton })}>
                                            <Form form={form} />
                                        </div>
                                    )}
                                </>
                            </AnimationFadeIn>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CtaFullSimple;
